import React from "react";
import { TextField, TextFieldProps } from "@mui/material";

interface InputNumberProps extends Omit<TextFieldProps, "type"> {
  /**
   * If true, prevents value change on mouse wheel scroll
   * @default true
   */
  preventScrollChange?: boolean;
  /**
   * If true, hides the browser's native spinner arrows
   * @default true
   */
  hideSpinner?: boolean;
  /**
   * If true, uses type="number", otherwise uses type="text" with numeric inputMode
   * @default false
   */
  useNumberType?: boolean;
  /**
   * If true, allows negative numbers
   * @default false
   */
  allowNegative?: boolean;
  /**
   * If true, allows decimal numbers
   * @default true
   */
  allowDecimal?: boolean;
  /**
   * Maximum number of decimal places
   * @default undefined (unlimited)
   */
  maxDecimalPlaces?: number;
}

const InputNumber = React.forwardRef<HTMLDivElement, InputNumberProps>(
  (
    {
      preventScrollChange = true,
      hideSpinner = true,
      useNumberType = false,
      allowNegative = false,
      allowDecimal = true,
      maxDecimalPlaces,
      onKeyDown,
      inputProps,
      sx,
      ...restProps
    },
    ref
  ) => {
    const handleBeforeInput = (e: any) => {
      const char = e.data;
      if (!char) return;

      // Build allowed characters pattern
      let allowedPattern = /^[0-9]$/;

      if (allowDecimal && allowNegative) {
        allowedPattern = /^[0-9.-]$/;
      } else if (allowDecimal) {
        allowedPattern = /^[0-9.]$/;
      } else if (allowNegative) {
        allowedPattern = /^[0-9-]$/;
      }

      // Prevent non-numeric characters
      if (!allowedPattern.test(char)) {
        e.preventDefault();
        return;
      }

      // Additional validation for decimal point
      if (char === "." && !allowDecimal) {
        e.preventDefault();
        return;
      }

      // Additional validation for minus sign
      if (char === "-" && !allowNegative) {
        e.preventDefault();
        return;
      }

      // Prevent multiple decimal points
      if (char === "." && (e.target as HTMLInputElement).value.includes(".")) {
        e.preventDefault();
        return;
      }

      // Prevent minus sign not at start
      if (char === "-" && (e.target as HTMLInputElement).selectionStart !== 0) {
        e.preventDefault();
        return;
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Prevent specific keys based on configuration
      const preventKeys: string[] = ["e", "E", "+"];

      if (!allowNegative) {
        preventKeys.push("-");
      }

      if (!allowDecimal) {
        preventKeys.push(".");
      }

      if (preventKeys.includes(e.key)) {
        e.preventDefault();
      }

      // Call the original onKeyDown if provided
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    const handleWheel = preventScrollChange
      ? (e: React.WheelEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          // Blur the input to prevent scroll change
          (e.target as HTMLInputElement).blur();
        }
      : undefined;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;

      // Filter out non-numeric characters based on configuration
      let filteredValue = value;

      if (allowDecimal && allowNegative) {
        // Allow digits, decimal point, and minus sign
        filteredValue = value.replace(/[^0-9.-]/g, "");
      } else if (allowDecimal && !allowNegative) {
        // Allow digits and decimal point only
        filteredValue = value.replace(/[^0-9.]/g, "");
      } else if (!allowDecimal && allowNegative) {
        // Allow digits and minus sign only
        filteredValue = value.replace(/[^0-9-]/g, "");
      } else {
        // Allow digits only
        filteredValue = value.replace(/[^0-9]/g, "");
      }

      // Ensure only one decimal point
      if (allowDecimal) {
        const parts = filteredValue.split(".");
        if (parts.length > 2) {
          filteredValue = parts[0] + "." + parts.slice(1).join("");
        }
      }

      // Ensure minus sign is only at the start
      if (allowNegative && filteredValue.includes("-")) {
        const minusCount = (filteredValue.match(/-/g) || []).length;
        if (minusCount > 1 || filteredValue.indexOf("-") > 0) {
          filteredValue = filteredValue.replace(/-/g, "");
          if (value.startsWith("-")) {
            filteredValue = "-" + filteredValue;
          }
        }
      }

      // If maxDecimalPlaces is set, enforce it
      if (maxDecimalPlaces !== undefined && filteredValue.includes(".")) {
        const parts = filteredValue.split(".");
        if (parts[1] && parts[1].length > maxDecimalPlaces) {
          filteredValue = `${parts[0]}.${parts[1].slice(0, maxDecimalPlaces)}`;
        }
      }

      // Update the input value
      e.target.value = filteredValue;

      // Call the original onChange if provided
      if (restProps.onChange) {
        restProps.onChange(e);
      }
    };

    const mergedInputProps = {
      inputMode: "numeric" as const,
      pattern: allowDecimal ? "[0-9]*\\.?[0-9]*" : "[0-9]*",
      onWheel: handleWheel,
      onBeforeInput: handleBeforeInput,
      ...inputProps,
    };

    const mergedSx = {
      ...sx,
      ...(hideSpinner && {
        "& input[type=number]": {
          "-moz-appearance": "textfield",
        },
        "& input[type=number]::-webkit-outer-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
        "& input[type=number]::-webkit-inner-spin-button": {
          "-webkit-appearance": "none",
          margin: 0,
        },
      }),
    };

    return (
      <TextField
        ref={ref}
        type={useNumberType ? "number" : "text"}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        inputProps={mergedInputProps}
        sx={mergedSx}
        {...restProps}
      />
    );
  }
);

InputNumber.displayName = "InputNumber";

export default InputNumber;
